import {useState, useEffect, useRef} from 'react'
import {SearchLgIcon, XCloseIcon} from '@components/ui/icon/Icon.tsx'
import {useSearchParams} from '@hooks/useSearchParams.ts'
import {QuerySearchParams} from '@/features/measures/services/types.ts'
import {routes} from '@utilities/constants/routes.tsx'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {SearchInput, SearchButton} from '@components/ui/search-bar/style.ts'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Button} from '@components/ui/button/Button.tsx'

interface SearchBarProps {
    isOpen: boolean
    onToggleSearch: (isOpen: boolean) => void
    isMobile: boolean
}

export const SearchBar = ({isOpen, onToggleSearch, isMobile}: SearchBarProps) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams(QuerySearchParams)
    const [searchQuery, setSearchQuery] = useState(searchParams.query || '')

    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        setSearchQuery(searchParams.query || '')
    }, [searchParams.query])

    const handleSearch = () => {
        if (searchQuery.trim() !== '') {
            const currentParams = new URLSearchParams(window.location.search)
            const filterParams = new URLSearchParams()

            // Store current pagination values for when we go back
            const currentPage = currentParams.get('page')
            const currentPerPage = currentParams.get('perPage')
            if (currentPage) filterParams.set('returnPage', currentPage)
            if (currentPerPage) filterParams.set('returnPerPage', currentPerPage)

            // Preserve all current filter parameters except pagination
            for (const [key, value] of currentParams.entries()) {
                if (key !== 'query' && key !== 'page' && key !== 'perPage') {
                    filterParams.set(key, value)
                }
            }

            // Add the search query
            filterParams.set('query', searchQuery)

            const queryString = filterParams.toString()
            navigate(`/search?${queryString}`, {replace: true})
        } else {
            setSearchParams({})
            navigate(routes.MEASURES.path)
        }
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        setSearchQuery(newValue)

        if (newValue.trim() === '') {
            setSearchParams({})
            navigate(routes.MEASURES.path)
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSearch()
        }
    }

    // Aggiunge l'event listener per Command + K (Mac) e Ctrl + K (Windows)
    useEffect(() => {
        const handleShortcut = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault()
                inputRef.current?.focus()
            }
        }

        document.addEventListener('keydown', handleShortcut)
        return () => {
            document.removeEventListener('keydown', handleShortcut)
        }
    }, [])

    return (
        <>
            {isMobile ? (
                isOpen ? (
                    <Flexbox align="center" gap={2}>
                        <SearchInput
                            ref={inputRef}
                            type="text"
                            placeholder={t('commons:search')}
                            inputSize="sm"
                            value={searchQuery}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                        />
                        {searchQuery.trim() !== '' && (
                            <SearchButton>
                                <Button variant="primaryGhost" onClick={handleSearch}>
                                    <Flexbox align="center" gap={1}>
                                        <SearchLgIcon />
                                    </Flexbox>
                                </Button>
                            </SearchButton>
                        )}
                        <XCloseIcon onClick={() => onToggleSearch(false)} />
                    </Flexbox>
                ) : (
                    <SearchLgIcon onClick={() => onToggleSearch(true)} />
                )
            ) : (
                <Flexbox align="center" gap={2}>
                    <SearchInput
                        ref={inputRef}
                        type="text"
                        placeholder={t('commons:search')}
                        inputSize="sm"
                        value={searchQuery}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                    />
                    {searchQuery.trim() !== '' && (
                        <SearchButton>
                            <Button variant="primaryGhost" onClick={handleSearch}>
                                <Flexbox align="center" gap={1}>
                                    <SearchLgIcon />
                                </Flexbox>
                            </Button>
                        </SearchButton>
                    )}
                </Flexbox>
            )}
        </>
    )
}
